import { Dispatch, SetStateAction, useId } from 'react'
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogOpenChangeEvent,
  DialogOpenChangeData,
  Button,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components'
import { Dismiss24Regular } from '@fluentui/react-icons'

interface ModalV2Props {
  modalHeader: string
  modalContent: JSX.Element
  isModalOpen: boolean
  setIsModalOpen: Dispatch<SetStateAction<boolean>> | undefined
}

const useStyles = makeStyles({
  surface: {
    minWidth: '300px',
    maxWidth: '450px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke1),
    ...shorthands.padding('16px', '24px'),
  },
  content: {
    ...shorthands.padding('16px', '24px'),
    maxHeight: '80vh',
    overflowY: 'auto',
  },
  headerText: {
    flex: '1',
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase600,
    margin: '0',
  },
  closeButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: tokens.colorNeutralForeground3,
    ':hover': {
      color: tokens.colorNeutralForeground2,
    },
    ...shorthands.padding('4px'),
    ...shorthands.margin('0'),
    backgroundColor: 'transparent',
    ...shorthands.border('none'),
  },
})

export const ModalV2 = ({
  modalHeader,
  modalContent,
  isModalOpen,
  setIsModalOpen,
}: ModalV2Props) => {
  const styles = useStyles()
  const titleId = useId()

  const handleOpenChange = (
    _event: DialogOpenChangeEvent,
    data: DialogOpenChangeData
  ) => {
    if (setIsModalOpen && !data.open) {
      setIsModalOpen(false)
    }
  }

  return (
    <Dialog
      open={isModalOpen}
      onOpenChange={handleOpenChange}
      modalType="modal"
      aria-labelledby={titleId}
    >
      <DialogSurface className={styles.surface}>
        <div className={styles.header}>
          <DialogTitle id={titleId} className={styles.headerText}>
            {modalHeader}
          </DialogTitle>
          {setIsModalOpen && (
            <Button
              data-testid="CloseModalBtn"
              appearance="subtle"
              icon={<Dismiss24Regular />}
              onClick={() => setIsModalOpen(false)}
              className={styles.closeButton}
              aria-label="Close"
            />
          )}
        </div>
        <DialogBody>
          <DialogContent className={styles.content}>
            {modalContent}
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}
